import { EventControllerApiFactory } from "@eu.mdotm.api/sphere";
import { getApiGen } from "$root/api/factory";
import env from "$root/env";
import { accessToken$, impersonateEmail$ } from "$root/functional-areas/session/atom";
import { user$ } from "$root/functional-areas/user";
import { needsMfaEnrollment } from "$root/functional-areas/user/mfa";
import { observe } from "@mdotm/shadcn/legacy/reactivity";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import { type ServerEventPayloadMap, eventBus } from "./bus";

function innerServerEvents(
	accessToken?: string,
	impersonateEmail?: string | null,
): {
	close(): void;
	restart(): void;
} {
	const isAuthenticated = accessToken;
	const timeBetweenRetry = isAuthenticated ? 10000 : 300000;
	let source: EventSource | null = null;
	let abortController: AbortController | null = null;

	const forwarders: { [key in keyof ServerEventPayloadMap]: (e: MessageEvent) => void } = {
		"investment-update": (e) => eventBus.emit("investment-update", JSON.parse(e.data)),
		"market-update": (e) => eventBus.emit("market-update", JSON.parse(e.data)),
		"investment-draft-configuration-update": (e) =>
			eventBus.emit("investment-draft-configuration-update", JSON.parse(e.data)),
		"benchmark-update": (e) => eventBus.emit("benchmark-update", JSON.parse(e.data)),
		"system-update": (e) => eventBus.emit("system-update", JSON.parse(e.data)),
		"user-update": (e) => eventBus.emit("user-update", JSON.parse(e.data)),
		"bulk-report-download": (e) => eventBus.emit("bulk-report-download", JSON.parse(e.data)),
		"investment-bulk-enhance-update": (e) => eventBus.emit("investment-bulk-enhance-update", JSON.parse(e.data)),
		"shared-entity": (e) => eventBus.emit("shared-entity", JSON.parse(e.data)),
		"deleted-entity": (e) => eventBus.emit("deleted-entity", JSON.parse(e.data)),
		"removed-entity": (e) => eventBus.emit("removed-entity", JSON.parse(e.data)),
		"modified-entity": (e) => eventBus.emit("modified-entity", JSON.parse(e.data)),
		"commentary-update": (e) => eventBus.emit("commentary-update", JSON.parse(e.data)),
		"investment-report-update": (e) => eventBus.emit("investment-report-update", JSON.parse(e.data)),
		"instrument-commentary-update": (e) => eventBus.emit("instrument-commentary-update", JSON.parse(e.data)),
		"instrument-propagation-schedule-changed": (e) =>
			eventBus.emit("instrument-propagation-schedule-changed", JSON.parse(e.data)),
		"instrument-propagation-started": (e) => eventBus.emit("instrument-propagation-started", JSON.parse(e.data)),
		"instrument-propagation-done": (e) => eventBus.emit("instrument-propagation-done", JSON.parse(e.data)),
		"investment-import-update": (e) => eventBus.emit("investment-import-update", JSON.parse(e.data)),
		// The non-portfolio import topics. `import-apply` targets universes, references, market views
		// and instruments too, and each has its own topic — a stream that forwarded only the
		// investment one left those imports finishing with nothing telling the app to refresh.
		"reference-import-update": (e) => eventBus.emit("reference-import-update", JSON.parse(e.data)),
		"universe-import-update": (e) => eventBus.emit("universe-import-update", JSON.parse(e.data)),
		"market-view-import-update": (e) => eventBus.emit("market-view-import-update", JSON.parse(e.data)),
		// "instrumens" is the backend's spelling (sic) — it's the wire name, so it has to match.
		"instrumens-import-update": (e) => eventBus.emit("instrumens-import-update", JSON.parse(e.data)),
		"proposal-execution-breached-constraint": (e) =>
			eventBus.emit("proposal-execution-breached-constraint", JSON.parse(e.data)),
		"cash-conversion-to-money-market": (e) => eventBus.emit("cash-conversion-to-money-market", JSON.parse(e.data)),
		"shared-entities": (e) => eventBus.emit("shared-entities", JSON.parse(e.data)),
		"smartx-sync": (e) => eventBus.emit("smartx-sync", JSON.parse(e.data)),
		"lab-compute": (e) => eventBus.emit("lab-compute", JSON.parse(e.data)),
	};

	function close() {
		if (restartTimeoutId !== null) {
			clearTimeout(restartTimeoutId);
			restartTimeoutId = null;
		}
		if (source) {
			// We shouldn't need this, but the BE would leave the EventSource
			// pending otherwise, so this is a temporary (?) fix.
			abortController?.abort(new Error("abort pending open request"));
			abortController = null;

			source.removeEventListener("open", handleOpen);
			source.removeEventListener("error", handleError);
			for (const eventName of Object.keys(forwarders) as (keyof ServerEventPayloadMap)[]) {
				source.removeEventListener(eventName, forwarders[eventName]);
			}
			source.close();
			source = null;
		}
	}

	function start() {
		if (isAuthenticated) {
			// EventSource can't send custom headers, so the impersonation carried by the
			// axios `X-Impersonate-Email` interceptor doesn't reach this stream. The BE
			// accepts the impersonated user's email as a query param instead, so the event
			// stream is scoped to the impersonated user rather than the impersonator.
			const registerUrl = new URL(`${env.apiOrigin}/v1/event/register`);
			registerUrl.searchParams.set("token", accessToken);
			if (impersonateEmail) {
				registerUrl.searchParams.set("impersonate", impersonateEmail);
			}
			source = isAuthenticated
				? new EventSource(registerUrl.toString())
				: new EventSource(`${env.apiOrigin}/v1/ext-event/register`);

			source.addEventListener("open", handleOpen);
			for (const eventName of Object.keys(forwarders) as (keyof ServerEventPayloadMap)[]) {
				source.addEventListener(eventName, forwarders[eventName]);
			}

			source.addEventListener("error", handleError);

			// We shouldn't need this, but the BE would leave the EventSource
			// pending otherwise, so this is a temporary (?) fix. (2/2)
			const newAbortController = new AbortController();
			abortController = newAbortController;
			setTimeout(
				unpromisify(async () => {
					await fetch(`${env.apiOrigin}/v1/event/send/open-channel`, {
						method: "POST",
						signal: newAbortController.signal,
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${accessToken}`,
							// This raw fetch bypasses the axios interceptor, so it must carry the
							// impersonation tag itself to open the impersonated user's channel.
							...(impersonateEmail ? { "X-Impersonate-Email": impersonateEmail } : {}),
						},
						body: JSON.stringify({
							level: "INFO",
							type: "NONE",
							userId: "",
							uuid: "",
							name: "",
							status: "",
						}),
					});
				}),
				1000,
			);
		}
	}

	function restart() {
		close();
		start();
	}

	let restartTimeoutId: ReturnType<typeof setTimeout> | null = null;

	function handleOpen() {
		console.log("Ready to receive server-side events");
		// Let listeners reconcile whatever they may have missed while the stream was down: this fires
		// on every (re)open, including the routine restarts on token renewal, and the restart discards
		// the browser's `Last-Event-ID` replay.
		eventBus.emit("server-events-stream-open");
	}

	function handleError(e: EventSourceEventMap["error"]) {
		console.error("Server Events error: ", e);
		let needRetry = true;
		if (restartTimeoutId !== null) {
			clearTimeout(restartTimeoutId);
			restartTimeoutId = null;
		}
		// Add an api call to check the Token Validity and force logout
		if (isAuthenticated) {
			const apiCheckCall = getApiGen(EventControllerApiFactory);
			const tokenCheck = (async () => {
				try {
					const check = await apiCheckCall.verify1();
					return check;
				} catch (err) {
					needRetry = false;
					console.warn("check failed, Logging Out", err);
				}
			})();
			console.log("Token Check", tokenCheck);
		}
		if (needRetry) {
			restartTimeoutId = setTimeout(() => {
				restart();
			}, timeBetweenRetry); // Restart after a defined time
		}
	}

	return {
		restart,
		close,
	};
}

export function initServerEvents(): void {
	let serverEvents: ReturnType<typeof innerServerEvents> | null = null;
	observe((get) => {
		const accessToken = get(accessToken$);
		// Also track the impersonation: it no longer swaps `accessToken$` (the
		// impersonator's token is kept), so without this the stream would never be
		// re-registered when impersonation starts or stops and would keep delivering
		// the impersonator's events.
		const impersonateEmail = get(impersonateEmail$);
		const user = get(user$);
		// Only open the stream for a fully-resolved, non-gated session. Requiring
		// `signedIn` (not just a token) avoids a race: `accessToken$` can be written
		// before `user$` resolves — by `authSessionInit`, or by the OIDC layer on
		// `addUserLoaded`/silent-renew — and a user gated on forced MFA enrollment is
		// blocked from every endpoint but `/mfa/enroll/*`. Reading `user$` here means
		// the stream (re)opens automatically once enrollment clears the gate.
		const canOpenStream = user.signedIn && !needsMfaEnrollment(user);
		serverEvents?.close();
		if (accessToken && canOpenStream) {
			serverEvents = innerServerEvents(accessToken, impersonateEmail);
			serverEvents.restart();
		}
	});
}
